import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Avatar,
    Badge,
    Box,
    Button,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    HStack,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Link,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    NumberInput,
    NumberInputField,
    Progress,
    SimpleGrid,
    Spacer,
    Text,
    Textarea,
    Tooltip,
    useDisclosure,
    useToast,
    VStack,
    useColorMode,
    Checkbox,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverHeader,
    PopoverBody,
    PopoverArrow,
    PopoverCloseButton,
    Stack,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import {
    AiOutlineSearch,
    AiOutlineLeft,
    AiOutlineRight,
    AiOutlineDelete,
    AiOutlineEdit,
    AiOutlineCheckCircle,
    AiOutlineUser,
    AiOutlineBank,
    AiOutlineCalendar,
    AiOutlinePlus,
    AiOutlinePartition,
    AiOutlineEllipsis,
    AiOutlineClose,
    AiOutlineCheck,
    AiOutlinePhone,
    AiOutlineMail,
    AiOutlineIdcard,
    AiOutlineFire,
    AiOutlineUserAdd,
    AiOutlineDatabase,
    AiOutlineWarning,
    AiOutlineContacts,
    AiOutlineInfoCircle,
    AiOutlineSchedule,
    AiOutlineEye,
    AiOutlineFilter,
    AiOutlineDollarCircle,
    AiOutlineMinus,
} from 'react-icons/ai';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useComercial, CANAIS, PIPELINE_DEFAULT_ID } from '../../../../contexts/ComercialContext';
import { useNogleTech } from '../../../../contexts/NogleTechContext';
import { usePermissions } from '../../../../contexts/AuthPermissionsContext';
import { useModalMinimizer } from '../../../../contexts/ModalMinimizerContext';
import { useAuth } from '../../../../hooks/auth';
import ConfirmDeleteDialog from '../../../../components/UI/ConfirmDeleteDialog';
import SearchableSelect from '../../../../components/UI/SearchableSelect';
import AnotacaoFormSection from '../../../../components/UI/AnotacaoFormSection';
import { criarAnotacao } from '../../../../services/anotacoes';

// ─── Constantes ──────────────────────────────────────────────────────────────

const pulseRed = keyframes`
  0% { transform: scale(0.9); opacity: 0.65; box-shadow: 0 0 0 0 rgba(252, 129, 129, 0.7); }
  50% { transform: scale(1.15); opacity: 1; box-shadow: 0 0 8px 3px rgba(252, 129, 129, 0.55); }
  100% { transform: scale(0.9); opacity: 0.65; box-shadow: 0 0 0 0 rgba(252, 129, 129, 0); }
`;

const pulseYellow = keyframes`
  0% { transform: scale(0.9); opacity: 0.65; box-shadow: 0 0 0 0 rgba(236, 201, 75, 0.7); }
  50% { transform: scale(1.15); opacity: 1; box-shadow: 0 0 8px 3px rgba(236, 201, 75, 0.55); }
  100% { transform: scale(0.9); opacity: 0.65; box-shadow: 0 0 0 0 rgba(236, 201, 75, 0); }
`;

const cardPulseRed = keyframes`
  0% { box-shadow: 0 0 6px rgba(252, 129, 129, 0.15), inset 0 0 3px rgba(252, 129, 129, 0.05); }
  50% { box-shadow: 0 0 12px rgba(252, 129, 129, 0.35), inset 0 0 6px rgba(252, 129, 129, 0.15); }
  100% { box-shadow: 0 0 6px rgba(252, 129, 129, 0.15), inset 0 0 3px rgba(252, 129, 129, 0.05); }
`;

const cardPulseYellow = keyframes`
  0% { box-shadow: 0 0 6px rgba(236, 201, 75, 0.15), inset 0 0 3px rgba(236, 201, 75, 0.05); }
  50% { box-shadow: 0 0 12px rgba(236, 201, 75, 0.35), inset 0 0 6px rgba(236, 201, 75, 0.15); }
  100% { box-shadow: 0 0 6px rgba(236, 201, 75, 0.15), inset 0 0 3px rgba(236, 201, 75, 0.05); }
`;

const PALETA_CORES = [
    '#9F7AEA', '#4299E1', '#ECC94B', '#ED8936', '#FC8181',
    '#02EA75', '#718096', '#F687B3', '#76E4F7', '#FBD38D',
    '#68D391', '#48BB78', '#63B3ED', '#B794F4', '#A0AEC0',
    '#FC8181', '#F6AD55', '#CBD5E0', '#FEB2B2', '#E9D8FD',
];

const PRIORIDADES = [
    { id: 'Alta', cor: '#FC8181' },
    { id: 'Média', cor: '#ECC94B' },
    { id: 'Baixa', cor: '#4299E1' },
];

const CARD_FORM_INICIAL = {
    titulo: '',
    contato: '',
    contatosSelecionados: [],
    empresa: '',
    valor: '',
    probabilidade: 50,
    dataInicio: '',
    dataFechamento: '',
    responsavel: '',
    assignee_id: null,
    canal: '',            // canal é obrigatório no salvar; sem default hardcoded (nome é editável em Controladores)
    prioridade: 'Média',
    observacoes: '',
    marcaId: null,        // id da marca vinda do ERP (NogleTechContext) — null se cliente novo
    clienteNovo: false,   // true quando o cliente NÃO existe em marcas/contatos
    compromissos: [],     // array de eventos vinculados (integra com o Calendário)
    result_id: null,
};

// Tipos de compromisso (agenda)
const TIPOS_COMPROMISSO = [
    { id: 'Reunião', cor: '#4299E1', icon: 'meeting' },
    { id: 'Ligação', cor: '#02EA75', icon: 'phone' },
    { id: 'Follow-up', cor: '#9F7AEA', icon: 'followup' },
    { id: 'Prazo', cor: '#FC8181', icon: 'deadline' },
    { id: 'Entrega', cor: '#ED8936', icon: 'delivery' },
    { id: 'Outro', cor: '#718096', icon: 'other' },
];

const COMPROMISSO_INICIAL = {
    tipo: 'Reunião',
    titulo: '',
    data: '',          // data de início (YYYY-MM-DD)
    dataFim: '',       // data de fim (vazio = mesmo dia)
    horaInicio: '09:00',
    horaFim: '10:00',
    local: '',
    descricao: '',
    concluido: false,
    employee_id: '',
};

const fmt = (v) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v || 0);

// ─── ColorPicker ─────────────────────────────────────────────────────────────

const ColorPicker = ({ value, onChange }) => (
    <Flex wrap="wrap" gap={2} mt={2}>
        {PALETA_CORES.map(cor => (
            <Box
                key={cor}
                w="22px" h="22px"
                borderRadius="md"
                bg={cor}
                cursor="pointer"
                border="2px solid"
                borderColor={value === cor ? 'white' : 'transparent'}
                boxShadow={value === cor ? `0 0 0 1px ${cor}` : 'none'}
                _hover={{ transform: 'scale(1.25)', boxShadow: `0 0 6px ${cor}80` }}
                transition="all 0.15s"
                onClick={() => onChange(cor)}
                flexShrink={0}
            />
        ))}
    </Flex>
);

// ─── EtapaTipoSelector (classifica a etapa: aberto / ganho / perdido) ─────────
const ETAPA_TIPOS = [
    { v: 'aberto', l: 'Em aberto', c: '#718096' },
    { v: 'ganho', l: 'Ganho', c: '#01E975' },
    { v: 'perdido', l: 'Perdido', c: '#FC8181' },
];

const EtapaTipoSelector = ({ value, onChange }) => (
    <HStack spacing={2}>
        {ETAPA_TIPOS.map(opt => {
            const active = (value || 'aberto') === opt.v;
            return (
                <Box
                    key={opt.v}
                    as="button"
                    type="button"
                    flex={1}
                    py={2}
                    borderRadius="lg"
                    fontSize="xs"
                    fontWeight="800"
                    border="1px solid"
                    bg={active ? `${opt.c}22` : 'transparent'}
                    color={active ? opt.c : 'textSecondary'}
                    borderColor={active ? opt.c : 'borderPrimary'}
                    transition="all .15s ease"
                    _hover={{ borderColor: opt.c, color: opt.c }}
                    onClick={() => onChange(opt.v)}
                >
                    {opt.l}
                </Box>
            );
        })}
    </HStack>
);

// ─── DetailItem (usado no modal de visualização) ─────────────────────────────

const DetailItem = ({ icon, label, value, valueColor }) => {
    const { colorMode } = useColorMode();
    return (
        <Box bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"} border="1px solid" borderColor="borderPrimary" borderRadius="lg" px={3} py={2.5}>
            <HStack spacing={1.5} mb={0.5}>
                <Icon as={icon} color="textSecondary" boxSize="11px" />
                <Text fontSize="9px" color="textSecondary" fontWeight="900" textTransform="uppercase" letterSpacing="1px">
                    {label}
                </Text>
            </HStack>
            <Text fontSize="sm" color={valueColor || 'textPrimary'} fontWeight="700" lineHeight="1.3">
                {value}
            </Text>
        </Box>
    );
};

// ─── SecaoLabel ──────────────────────────────────────────────────────────────

const SecaoLabel = ({ label }) => (
    <HStack spacing={3} mt={1}>
        <Text fontSize="9px" color="textMuted" fontWeight="800" textTransform="uppercase" letterSpacing="2px" whiteSpace="nowrap">
            {label}
        </Text>
        <Divider borderColor="borderPrimary" />
    </HStack>
);

// ─── Pipeline (main) ─────────────────────────────────────────────────────────

const NegocioModal = ({ isOpen, onClose, cardEditando, pipelineAtivo, defaultColunaId }) => {
    const { colorMode } = useColorMode();
    const {
        pipelines, pipelineCards, contatos, atividades,
        addPipeline, updatePipeline, deletePipeline,
        addColuna, updateColuna, deleteColuna,
        addPipelineCard, updatePipelineCard, moverCard,
        addCompromisso, updateCompromisso, deleteCompromisso,
        addAtividade, updateAtividade, resultadosNegocio, fetchResultadosNegocio,
        canaisDropdown,
    } = useComercial();
    const navigate = useNavigate();

    // Canais de origem do banco (CONTROLADORES_COMERCIAL_NOGLE type "canais");
    // fallback para a lista fixa CANAIS enquanto o dropdown não carrega.
    const opcoesCanal = useMemo(
        () => (canaisDropdown && canaisDropdown.length > 0 ? canaisDropdown.map(c => c.nome) : CANAIS),
        [canaisDropdown]
    );

    // Marcas vindas da API Cartman (ERP) — usadas no autocomplete da empresa
    const { marcas } = useNogleTech();
    const { employees } = usePermissions();
    const { user } = useAuth();

    const currentEmployee = useMemo(() => {
        if (!user || !employees || employees.length === 0) return null;
        const lowerEmail = user.email?.toLowerCase();
        return employees.find(e => e.email && e.email.toLowerCase() === lowerEmail) || null;
    }, [user, employees]);

    const toast = useToast();

    useEffect(() => {
        if (fetchResultadosNegocio) {
            fetchResultadosNegocio();
        }
    }, [fetchResultadosNegocio]);

    const [atvPage, setAtvPage] = useState(1);
    const atvItemsPerPage = 5;

    // ── Estado local ───────────────────────────────────────────────
    const [pipelineAtivoId, setPipelineAtivoId] = useState(() =>
        localStorage.getItem('comercial_pipeline_ativo') || PIPELINE_DEFAULT_ID
    );
    const [busca, setBusca] = useState('');
    const [buscaContato, setBuscaContato] = useState('');
    const [filterEmployeeIds, setFilterEmployeeIds] = useState([]);
    const [filterSearch, setFilterSearch] = useState('');

    const handleSetPipelineAtivo = (id) => {
        setPipelineAtivoId(id);
        localStorage.setItem('comercial_pipeline_ativo', id);
    };

    // ── Cards do pipeline ativo ────────────────────────────────────
    const cardsDoBoard = useMemo(() => {
        if (!pipelineAtivo) return [];
        const q = busca.toLowerCase();
        return pipelineCards.filter(c => {
            if (c.completed) return false; // concluídos saem do board → vão pra tela "Concluídas"
            if (c.pipelineId !== pipelineAtivo.id) return false;
            if (filterEmployeeIds.length > 0 && !filterEmployeeIds.includes(String(c.assignee_id))) return false;
            if (!q) return true;
            return (
                c.titulo?.toLowerCase().includes(q) ||
                c.contato?.toLowerCase().includes(q) ||
                c.empresa?.toLowerCase().includes(q)
            );
        });
    }, [pipelineCards, pipelineAtivo, busca, filterEmployeeIds]);

    const cardsPorColuna = useMemo(() => {
        const result = {};
        (pipelineAtivo?.colunas || []).forEach(col => { result[col.id] = []; });
        cardsDoBoard.forEach(c => {
            if (result[c.colunaId] !== undefined) result[c.colunaId].push(c);
        });
        return result;
    }, [cardsDoBoard, pipelineAtivo]);

    const totalPipeline = useMemo(() =>
        cardsDoBoard.reduce((acc, c) => acc + (Number(c.valor) || 0), 0)
        , [cardsDoBoard]);

    // Agrega TODOS os compromissos pendentes de TODOS os cards do pipeline ativo
    const proximosEventos = useMemo(() => {
        if (!pipelineAtivo) return [];
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const eventos = [];
        cardsDoBoard.forEach(card => {
            (card.compromissos || []).forEach(comp => {
                if (comp.concluido || !comp.data) return;
                eventos.push({
                    ...comp,
                    cardId: card.id,
                    cardTitulo: card.titulo,
                    cardEmpresa: card.empresa,
                    cardColunaId: card.colunaId,
                    dataObj: new Date(comp.data + 'T' + (comp.horaInicio || comp.hora || '00:00')),
                });
            });
        });
        return eventos
            .sort((a, b) => a.dataObj - b.dataObj)
            .slice(0, 8);
    }, [cardsDoBoard, pipelineAtivo]);

    // ── Modal: Card (criar/editar) ────────────────────────────────
    const isCardOpen = isOpen;
    const onCardClose = onClose;
    const onCardOpen = () => { }; // no-op: modal é controlado pela prop isOpen (resíduo do Pipeline)
    const [internalCardEditando, setInternalCardEditando] = useState(null);
    const [cardColunaId, setCardColunaId] = useState('');
    // Funil selecionado no formulário (dropdown "Funil"): permite mover o negócio
    // de funil — o banco deriva o funil da coluna (colunaId) escolhida ao salvar.
    // Padrão: funil do card (edição) ou o funil recebido via prop (criação).
    const [funilFormId, setFunilFormId] = useState(null);
    const funilForm = (pipelines || []).find(p => String(p.id) === String(funilFormId)) || pipelineAtivo;
    const colunasFormulario = [...(funilForm?.colunas || [])].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
    const [formCard, setFormCard] = useState(CARD_FORM_INICIAL);
    const [novaAnotacao, setNovaAnotacao] = useState('');

    useEffect(() => {
        if (isOpen) {
            setNovaAnotacao('');
            setInternalCardEditando(cardEditando || null);
            setCardColunaId(cardEditando?.colunaId || defaultColunaId || (pipelineAtivo?.colunas?.[0]?.id) || '');
            setFunilFormId(cardEditando?.pipelineId || pipelineAtivo?.id || null);
            if (cardEditando) {
                setFormCard({
                    titulo: cardEditando.titulo || '',
                    contato: cardEditando.contato || '',
                    contatosSelecionados: typeof cardEditando.contato === 'string' ? cardEditando.contato.split(',').map(c => c.trim()).filter(Boolean) : [],
                    empresa: cardEditando.empresa || '',
                    marcaId: cardEditando.company_id || null,
                    company_id: cardEditando.company_id || null,
                    valor: cardEditando.valor || 0,
                    probabilidade: cardEditando.probabilidade ?? 50,
                    dataInicio: cardEditando.dataInicio || '',
                    dataFechamento: cardEditando.dataFechamento || '',
                    responsavel: cardEditando.responsavel || '',
                    assignee_id: cardEditando.assignee_id || null,
                    canal: cardEditando.canal || '',
                    prioridade: cardEditando.prioridade || 'Média',
                    observacoes: cardEditando.observacoes || '',
                    clienteNovo: !cardEditando.company_id,
                    compromissos: cardEditando.compromissos || [],
                    // Sem estes dois campos a edição via Negócios "esquecia" o estado
                    // do card: o Resultado voltava a "Em aberto" e o meetLink era
                    // apagado no banco a cada salvamento.
                    result_id: cardEditando.result_id ?? null,
                    meetLink: cardEditando.meetLink || '',
                });
            } else {
                setFormCard({ ...CARD_FORM_INICIAL, colunaId: defaultColunaId || (pipelineAtivo?.colunas?.[0]?.id) || '' });
            }
        }
    }, [isOpen, cardEditando, defaultColunaId, pipelineAtivo]);


    // ── Modal: Visualizar Card (read-only) ────────────────────────
    const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
    const [cardVisualizando, setCardVisualizando] = useState(null);

    // ── Modal: Confirmar Conclusão (Ganho/Perdido) ────────────────
    const { isOpen: isConcluirOpen, onOpen: onConcluirOpen, onClose: onConcluirClose } = useDisclosure();
    const [cardConcluindo, setCardConcluindo] = useState(null);

    // ── Confirmação padrão: remover compromisso da agenda ─────────
    const { isOpen: isDelCompOpen, onOpen: onDelCompOpen, onClose: onDelCompClose } = useDisclosure();
    const [compRemovendo, setCompRemovendo] = useState(null);

    // ── AlertDialog: Confirmar criação com empresa nova/externa ───
    const { isOpen: isConfirmEmpresaOpen, onOpen: onConfirmEmpresaOpen, onClose: onConfirmEmpresaClose } = useDisclosure();
    const confirmEmpresaRef = React.useRef(null);

    // ── Drag & Drop ───────────────────────────────────────────────
    const [draggedCardId, setDraggedCardId] = useState(null);
    const [draggedAtividade, setDraggedAtividade] = useState(null);
    const [atividadeVinculoPendente, setAtividadeVinculoPendente] = useState(null);
    const [dragOverColId, setDragOverColId] = useState(null);

    const handleDragStart = useCallback((cardId) => {
        setDraggedCardId(cardId);
    }, []);

    const handleDragEnd = useCallback(() => {
        setDraggedCardId(null);
        setDraggedAtividade(null);
        setDragOverColId(null);
    }, []);

    const handleDragOverColuna = useCallback((e, colunaId) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = draggedAtividade ? 'copy' : 'move';
        if (dragOverColId !== colunaId) setDragOverColId(colunaId);
    }, [dragOverColId, draggedAtividade]);

    // Soltar uma ATIVIDADE numa coluna → ABRE o modal de novo negócio já pré-preenchido
    // (etapa + empresa + responsável da atividade). O vínculo é feito ao salvar.
    const abrirNovoNegocioDeAtividade = useCallback((atividade, colunaId) => {
        setInternalCardEditando(null);
        setCardColunaId(colunaId);
        setFunilFormId(pipelineAtivo?.id || null);
        setAtividadeVinculoPendente(atividade);
        setFormCard({
            ...CARD_FORM_INICIAL,
            colunaId,
            titulo: atividade.empresa || atividade.nome || '',
            empresa: atividade.empresa || '',
            marcaId: atividade.company_id || null,
            company_id: atividade.company_id || null,
            responsavel: atividade.responsavel || '',
            assignee_id: atividade.assignee_id || null,
            contatosSelecionados: [],
        });
        setEmpresaDropdownAberto(false);
        onCardOpen();
    }, [onCardOpen, pipelineAtivo]);

    const handleDropColuna = useCallback((e, colunaId) => {
        e.preventDefault();
        if (draggedAtividade) {
            abrirNovoNegocioDeAtividade(draggedAtividade, colunaId);
        } else if (draggedCardId) {
            const card = pipelineCards.find(c => c.id === draggedCardId);
            if (card && card.colunaId !== colunaId) {
                moverCard(draggedCardId, colunaId);
                toast({ title: 'Negócio movido', status: 'success', duration: 1500 });
            }
        }
        setDraggedCardId(null);
        setDraggedAtividade(null);
        setDragOverColId(null);
    }, [draggedAtividade, abrirNovoNegocioDeAtividade, draggedCardId, pipelineCards, moverCard, toast]);

    // Busca de empresa com autocomplete (Marcas ERP + Contatos + fallback)
    const [empresaDropdownAberto, setEmpresaDropdownAberto] = useState(false);
    const empresaInputRef = useRef(null);

    // Marcas filtradas (ERP — fonte primária)
    const marcasFiltradas = useMemo(() => {
        const q = formCard.empresa?.toLowerCase() || '';
        if (q.length < 1) return [];
        return (marcas || [])
            .filter(m => m.nome?.toLowerCase().includes(q))
            .slice(0, 5);
    }, [marcas, formCard.empresa]);

    // Contatos filtrados (CRM local — fonte secundária; exclui empresas já listadas em marcas)
    const contatosFiltrados = useMemo(() => {
        const q = formCard.empresa?.toLowerCase() || '';
        if (q.length < 1) return [];
        const nomesMarcas = new Set(marcasFiltradas.map(m => m.nome?.toLowerCase()));
        const seen = new Set();
        return contatos
            .filter(c => {
                const empresaLower = c.empresa?.toLowerCase();
                if (!empresaLower || nomesMarcas.has(empresaLower)) return false;
                if (seen.has(empresaLower)) return false;
                seen.add(empresaLower);
                return (
                    c.empresa?.toLowerCase().includes(q) ||
                    c.nome?.toLowerCase().includes(q)
                );
            })
            .slice(0, 4);
    }, [contatos, formCard.empresa, marcasFiltradas]);

    const temResultados = marcasFiltradas.length > 0 || contatosFiltrados.length > 0;

    const [contatoFocado, setContatoFocado] = useState(false);
    const [responsavelFocado, setResponsavelFocado] = useState(false);

    const contatosSugeridos = useMemo(() => {
        const query = buscaContato?.trim().toLowerCase();
        if (!query) return [];
        const queryDigits = query.replace(/\D/g, '');
        const hasExactMatch = contatos.some(c =>
            c.nome?.toLowerCase() === query ||
            (queryDigits && c.telefone?.replace(/\D/g, '') === queryDigits)
        );
        if (hasExactMatch) return [];
        return contatos.filter(c => {
            const nameMatch = c.nome?.toLowerCase().includes(query);
            const phoneDigits = c.telefone?.replace(/\D/g, '') || '';
            const phoneMatch = queryDigits && phoneDigits.includes(queryDigits);
            return nameMatch || phoneMatch;
        }).slice(0, 8);
    }, [contatos, buscaContato]);

    const contatoNaoEncontrado = useMemo(() => {
        const query = buscaContato?.trim().toLowerCase();
        if (!query) return false;
        const queryDigits = query.replace(/\D/g, '');
        return !contatos.some(c => {
            const nameMatch = c.nome?.toLowerCase().includes(query);
            const phoneDigits = c.telefone?.replace(/\D/g, '') || '';
            const phoneMatch = queryDigits && phoneDigits.includes(queryDigits);
            return nameMatch || phoneMatch;
        });
    }, [contatos, buscaContato]);

    const responsaveisSugeridos = useMemo(() => {
        const query = formCard.responsavel?.trim().toLowerCase();
        if (!query) return [];
        const list = (employees || []).filter(e => e.isWhitelisted !== false && e.isWhitelisted !== 0);
        if (list.some(e => e.name?.toLowerCase() === query)) return [];
        return list.filter(e => e.name?.toLowerCase().includes(query)).slice(0, 8);
    }, [employees, formCard.responsavel]);

    const handleSelecionarContatoCard = useCallback((c) => {
        const marca = c.empresa ? (marcas || []).find(m => m.nome?.toLowerCase() === c.empresa?.toLowerCase()) : null;
        setFormCard(p => {
            const novosContatos = p.contatosSelecionados && p.contatosSelecionados.includes(c.nome) ? p.contatosSelecionados : [...(p.contatosSelecionados || []), c.nome];
            return {
                ...p,
                contatosSelecionados: novosContatos,
                contato: novosContatos.join(', '),
                empresa: marca ? marca.nome : p.empresa, // Only auto-fill if linked brand (marca) is registered in the ERP
                marcaId: marca ? marca.id : p.marcaId,
                clienteNovo: marca ? false : p.clienteNovo,
            };
        });
        setBuscaContato('');
        setContatoFocado(false);
    }, [marcas]);

    const handleSelecionarResponsavelCard = useCallback((emp) => {
        setFormCard(p => ({
            ...p,
            responsavel: emp.name,
            assignee_id: emp.id
        }));
        setResponsavelFocado(false);
    }, []);

    // Marca vinculada ao card (se houver marcaId)
    const marcaVinculada = useMemo(() => {
        if (!formCard.marcaId) return null;
        return (marcas || []).find(m => m.id === formCard.marcaId) || null;
    }, [marcas, formCard.marcaId]);

    // Contato encontrado por nome de empresa (para mostrar telefone/e-mail)
    const contatoSelecionado = useMemo(() => {
        if (!formCard.empresa && !formCard.contato) return null;
        return contatos.find(c =>
            (formCard.empresa && c.empresa === formCard.empresa) ||
            (formCard.contato && c.nome === formCard.contato)
        ) || null;
    }, [contatos, formCard.empresa, formCard.contato]);

    const handleSelecionarMarca = useCallback((marca) => {
        setFormCard(p => ({
            ...p,
            empresa: marca.nome,
            contato: p.contato || marca.responsavel_marca_nome || '',
            marcaId: marca.id,
            clienteNovo: false,
        }));
        setEmpresaDropdownAberto(false);
    }, []);

    const handleSelecionarContato = useCallback((contato) => {
        const marca = contato.empresa ? (marcas || []).find(m => m.nome?.toLowerCase() === contato.empresa?.toLowerCase()) : null;
        setFormCard(p => {
            const novosContatos = p.contatosSelecionados && p.contatosSelecionados.includes(contato.nome) ? p.contatosSelecionados : [...(p.contatosSelecionados || []), contato.nome];
            return {
                ...p,
                empresa: marca ? marca.nome : p.empresa,
                contatosSelecionados: novosContatos,
                contato: novosContatos.join(', '),
                canal: contato.canal || p.canal,
                marcaId: marca ? marca.id : p.marcaId,
                clienteNovo: marca ? false : p.clienteNovo,
            };
        });
        setBuscaContato('');
        setEmpresaDropdownAberto(false);
    }, [marcas]);

    const handleUsarClienteNovo = useCallback(() => {
        setFormCard(p => ({
            ...p,
            marcaId: null,
            clienteNovo: true,
        }));
        setEmpresaDropdownAberto(false);
    }, []);

    const handleAbrirVisualizar = useCallback((card) => {
        setCardVisualizando(card);
        onViewOpen();
    }, [onViewOpen]);

    const handleFecharVisualizar = useCallback(() => {
        setCardVisualizando(null);
        onViewClose();
    }, [onViewClose]);

    // ── Gestão de Compromissos (Agenda) ──────────────────────────
    const [novoCompromissoAberto, setNovoCompromissoAberto] = useState(false);
    const [novoCompromisso, setNovoCompromisso] = useState(COMPROMISSO_INICIAL);
    const [editandoCompromissoId, setEditandoCompromissoId] = useState(null);

    const handleEditarCompromisso = useCallback((comp) => {
        setNovoCompromisso({
            titulo: comp.titulo || '',
            tipo: comp.tipo || TIPOS_COMPROMISSO[0].id,
            data: comp.data || '',
            dataFim: comp.dataFim || comp.data || '',
            horaInicio: comp.horaInicio || comp.hora || '00:00',
            horaFim: comp.horaFim || '',
            local: comp.local || '',
            descricao: comp.descricao || '',
            concluido: !!comp.concluido
        });
        setEditandoCompromissoId(comp.id);
        setNovoCompromissoAberto(true);
    }, []);

    // Mapeia um item da agenda do negócio → payload de ATIVIDADE (evento de calendário).
    // Responsável do negócio é o padrão; vincula ao negócio via cardId/ligarPipeline.
    const compromissoParaAtividade = useCallback((comp, cardId) => ({
        nome: comp.titulo,
        dataInicio: comp.data,
        horaInicio: comp.horaInicio || '00:00',
        dataFim: comp.dataFim || comp.data,
        horaFim: comp.horaFim || comp.horaInicio || '00:00',
        tipo: comp.tipo,
        status: 'pendente',
        assignee_id: comp.employee_id || formCard.assignee_id || null,
        company_id: formCard.company_id || formCard.marcaId || null,
        observacao: [comp.local, comp.descricao].filter(Boolean).join(' · '),
        ligarPipeline: true,
        cardId: cardId != null ? cardId : null,
    }), [formCard.assignee_id, formCard.company_id, formCard.marcaId]);

    const handleAdicionarCompromisso = useCallback(async () => {
        if (!novoCompromisso.titulo?.trim() || !novoCompromisso.data) {
            toast({ title: 'Título e data são obrigatórios', status: 'warning', duration: 2000 });
            return;
        }
        const dataFim = novoCompromisso.dataFim || novoCompromisso.data;
        if (dataFim < novoCompromisso.data) {
            toast({ title: 'A data de fim não pode ser anterior ao início', status: 'warning', duration: 2500 });
            return;
        }
        const hojeStr = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD local
        if (novoCompromisso.data < hojeStr) {
            toast({ title: 'Não é possível agendar em uma data passada', status: 'warning', duration: 2500 });
            return;
        }
        if (dataFim === novoCompromisso.data && novoCompromisso.horaInicio && novoCompromisso.horaFim
            && novoCompromisso.horaFim < novoCompromisso.horaInicio) {
            toast({ title: 'O horário de fim deve ser após o início', status: 'warning', duration: 2500 });
            return;
        }

        const compData = { ...novoCompromisso, dataFim };

        if (editandoCompromissoId) {
            const isTempId = String(editandoCompromissoId).startsWith('temp-') || String(editandoCompromissoId).startsWith('ev-');
            if (internalCardEditando && !isTempId) {
                try {
                    await updateCompromisso(internalCardEditando.id, editandoCompromissoId, {
                        titulo: compData.titulo,
                        tipo: compData.tipo,
                        data: compData.data,
                        dataFim: compData.dataFim,
                        horaInicio: compData.horaInicio,
                        horaFim: compData.horaFim,
                        local: compData.local,
                        descricao: compData.descricao
                    });
                    setFormCard(p => ({
                        ...p,
                        compromissos: (p.compromissos || []).map(c => 
                            c.id === editandoCompromissoId ? { ...compData, id: editandoCompromissoId } : c
                        )
                    }));
                    toast({ title: 'Compromisso atualizado', status: 'success', duration: 2000 });
                } catch (err) {
                    toast({ title: 'Erro ao atualizar compromisso', status: 'error', duration: 2000 });
                }
            } else {
                setFormCard(p => ({
                    ...p,
                    compromissos: (p.compromissos || []).map(c => 
                        c.id === editandoCompromissoId ? { ...compData, id: editandoCompromissoId } : c
                    )
                }));
            }
            setEditandoCompromissoId(null);
        } else {
            if (internalCardEditando) {
                try {
                    await addAtividade(compromissoParaAtividade(compData, internalCardEditando.id));
                    setFormCard(p => ({
                        ...p,
                        compromissos: [
                            ...(p.compromissos || []),
                            { ...compData, id: `ev-${Date.now()}` }
                        ]
                    }));
                    toast({ title: 'Evento criado no calendário', status: 'success', duration: 2000 });
                } catch (err) {
                    toast({ title: 'Erro ao agendar compromisso', status: 'error', duration: 2000 });
                }
            } else {
                const tempId = `temp-${Date.now()}`;
                setFormCard(p => ({
                    ...p,
                    compromissos: [
                        ...(p.compromissos || []),
                        { ...compData, id: tempId }
                    ]
                }));
            }
        }

        setNovoCompromisso(COMPROMISSO_INICIAL);
        setNovoCompromissoAberto(false);
    }, [novoCompromisso, internalCardEditando, editandoCompromissoId, addAtividade, updateCompromisso, compromissoParaAtividade, toast]);

    const handleRemoverCompromisso = useCallback(async (compId) => {
        if (internalCardEditando) {
            try {
                await deleteCompromisso(compId);
                setFormCard(p => ({
                    ...p,
                    compromissos: (p.compromissos || []).filter(c => c.id !== compId),
                }));
                toast({ title: 'Compromisso removido', status: 'info', duration: 2000 });
            } catch (err) {
                toast({ title: 'Erro ao remover compromisso', status: 'error', duration: 2000 });
            }
        } else {
            setFormCard(p => ({
                ...p,
                compromissos: (p.compromissos || []).filter(c => c.id !== compId),
            }));
        }
    }, [internalCardEditando, deleteCompromisso, toast]);

    const handleConfirmarRemoverCompromisso = useCallback(() => {
        if (compRemovendo) handleRemoverCompromisso(compRemovendo.id);
        setCompRemovendo(null);
        onDelCompClose();
    }, [compRemovendo, handleRemoverCompromisso, onDelCompClose]);

    const handleToggleCompromissoConcluido = useCallback(async (compId) => {
        const target = (formCard.compromissos || []).find(c => c.id === compId);
        if (!target) return;

        const novoEstadoConcluido = !target.concluido;

        if (internalCardEditando) {
            try {
                await updateCompromisso(internalCardEditando.id, compId, { concluido: novoEstadoConcluido });
                setFormCard(p => ({
                    ...p,
                    compromissos: (p.compromissos || []).map(c =>
                        c.id === compId ? { ...c, concluido: novoEstadoConcluido } : c
                    ),
                }));
            } catch (err) {
                toast({ title: 'Erro ao atualizar status', status: 'error', duration: 2000 });
            }
        } else {
            setFormCard(p => ({
                ...p,
                compromissos: (p.compromissos || []).map(c =>
                    c.id === compId ? { ...c, concluido: novoEstadoConcluido } : c
                ),
            }));
        }
    }, [internalCardEditando, formCard, updateCompromisso, toast]);

    const handleConfirmarConclusao = useCallback(async (status, resultId, resultRole) => {
        if (!cardConcluindo) return;
        const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
        const colunas = pipelineAtivo?.colunas || [];
        // Resultado + papel: preferir o que veio do chooser; senão resolve pelo nome.
        const resObj = (resultId && (resultadosNegocio || []).find(r => String(r.id) === String(resultId)))
            || (resultadosNegocio || []).find(r => norm(r.nome).includes(norm(status)) || norm(status).includes(norm(r.nome)));
        const papel = resultRole != null ? Number(resultRole) : (resObj ? Number(resObj.role) : null);

        // Coluna de destino pelo PAPEL (1 ganho · 2 perdido); fallback pelo nome.
        let colTarget = null;
        if (papel === 1) colTarget = colunas.find(col => col.tipo === 'ganho');
        else if (papel === 2) colTarget = colunas.find(col => col.tipo === 'perdido');
        if (!colTarget) colTarget = colunas.find(col => norm(col.label || col.nome).includes(norm(status)));

        try {
            // Regra: resultado ativo ⟺ concluído. Coluna de destino é opcional
            // (resultado neutro sem coluna conclui onde está).
            await updatePipelineCard(cardConcluindo.id, {
                completed: 1,
                ...(resObj ? { result_id: resObj.id } : {}),
                ...(colTarget ? { colunaId: colTarget.id } : {})
            });
            toast({
                title: `Negócio concluído como ${String(status).toUpperCase()}!`,
                status: papel === 1 ? 'success' : 'info',
                duration: 2500
            });

            // Fecha as duas modals
            onConcluirClose();
            setCardConcluindo(null);
            handleFecharVisualizar();
        } catch (err) {
            toast({ title: 'Erro ao concluir negócio', status: 'error', duration: 2500 });
        }
    }, [cardConcluindo, pipelineAtivo, updatePipelineCard, onConcluirClose, handleFecharVisualizar, toast, resultadosNegocio]);

    const handleAbrirCard = useCallback((card = null, colunaId = '') => {
        setInternalCardEditando(card);
        setCardColunaId(card ? card.colunaId : colunaId);
        setFunilFormId(card ? (card.pipelineId || pipelineAtivo?.id || null) : (pipelineAtivo?.id || null));
        setNovaAnotacao('');
        // Garante que compromissos sempre exista como array
        setFormCard(card
            ? {
                ...card,
                dataInicio: card.dataInicio || '',
                dataFechamento: card.dataFechamento || '',
                compromissos: card.compromissos || [],
                contatosSelecionados: card.contato ? card.contato.split(',').map(c => c.trim()).filter(c => c) : []
            }
            : {
                ...CARD_FORM_INICIAL,
                colunaId,
                responsavel: '',
                assignee_id: null,
                contatosSelecionados: []
            }
        );
        setEmpresaDropdownAberto(false);
        setNovoCompromissoAberto(false);
        setNovoCompromisso(COMPROMISSO_INICIAL);
        setBuscaContato('');
        onCardOpen();
    }, [onCardOpen, pipelineAtivo]);

    const { minimizeModal, removeMinimizedModal, consumePendingRestore } = useModalMinimizer();

    const handleMinimizarCard = useCallback(() => {
        onCardClose();
        const modalId = 'negocio-modal';
        const title = internalCardEditando ? 'Editar Negócio' : 'Novo Negócio';
        const subtitle = formCard.titulo || formCard.empresa || 'Rascunho de Negócio';
        minimizeModal({
            id: modalId,
            title,
            subtitle,
            icon: AiOutlinePartition,
            route: '/comercial/negocios',
            formData: { formCard, internalCardEditando, cardColunaId, funilFormId, novaAnotacao },
            onExpand: () => {
                onCardOpen();
            },
            onClose: () => {
                setInternalCardEditando(null);
                setFormCard(CARD_FORM_INICIAL);
                setNovaAnotacao('');
                setEmpresaDropdownAberto(false);
                setNovoCompromissoAberto(false);
                setNovoCompromisso(COMPROMISSO_INICIAL);
                setAtividadeVinculoPendente(null);
            }
        });
    }, [onCardClose, onCardOpen, internalCardEditando, formCard, cardColunaId, funilFormId, novaAnotacao, minimizeModal]);

    useEffect(() => {
        const pendingData = consumePendingRestore('negocio-modal');
        if (pendingData) {
            if (pendingData.formCard) setFormCard(pendingData.formCard);
            if (pendingData.internalCardEditando !== undefined) setInternalCardEditando(pendingData.internalCardEditando);
            if (pendingData.cardColunaId !== undefined) setCardColunaId(pendingData.cardColunaId);
            if (pendingData.funilFormId !== undefined) setFunilFormId(pendingData.funilFormId);
            if (pendingData.novaAnotacao !== undefined) setNovaAnotacao(pendingData.novaAnotacao);
            onCardOpen();
        }
    }, [consumePendingRestore, onCardOpen]);

    const handleFecharCard = useCallback(() => {
        setInternalCardEditando(null);
        setFormCard(CARD_FORM_INICIAL);
        setNovaAnotacao('');
        setEmpresaDropdownAberto(false);
        setNovoCompromissoAberto(false);
        setNovoCompromisso(COMPROMISSO_INICIAL);
        setAtividadeVinculoPendente(null);
        removeMinimizedModal('negocio-modal');
        onCardClose();
    }, [onCardClose, removeMinimizedModal]);

    const handleSalvarCard = useCallback(async (skipConfirmEmpresa = false) => {
        if (!formCard.titulo?.trim()) {
            toast({ title: 'Título obrigatório', status: 'warning', duration: 2000 });
            return;
        }
        if (contatoNaoEncontrado) {
            toast({ title: 'O contato precisa estar cadastrado na lista de contatos', status: 'warning', duration: 3000 });
            return;
        }

        let finalAssigneeId = formCard.assignee_id;
        if (!finalAssigneeId && formCard.responsavel) {
            const foundEmp = (employees || []).find(e => e.name?.toLowerCase() === formCard.responsavel.trim().toLowerCase());
            if (foundEmp) {
                finalAssigneeId = foundEmp.id;
            }
        }

        // Link da reunião (Google saiu): pega o "Local ou link" de um compromisso de Reunião
        // e grava na coluna meet_link do card (visível no card e na tela Concluídas).
        const reuniaoLink = (formCard.compromissos || [])
            .find(c => String(c.tipo || '').toLowerCase().includes('reuni') && c.local)?.local;

        // A etapa precisa pertencer ao funil escolhido no dropdown "Funil"; card
        // "sem funil" (coluna órfã) adota a primeira etapa do funil selecionado.
        const colunaPertenceAoFunil = (funilForm?.colunas || []).some(c => String(c.id) === String(cardColunaId));
        const targetColunaId = (colunaPertenceAoFunil ? cardColunaId : null) || funilForm?.colunas?.[0]?.id;
        // Resultado selecionado = card concluído (sai da pipeline, vive em Relatórios).
        // Sem resultado ("Em aberto") = card aberto (volta ao board na etapa escolhida).
        // Antes o completed ficava congelado no valor antigo do card e escolher
        // "Em aberto" nunca devolvia o card ao Kanban.
        const completed = !!formCard.result_id;

        // ── Campos obrigatórios do Negócio ──
        const empresaOk = !!(formCard.company_id || formCard.marcaId || formCard.empresa?.trim());
        const contatoOk = !!(formCard.contato?.trim() || (formCard.contatosSelecionados || []).length > 0);
        if (!empresaOk) { toast({ title: 'Empresa / marca obrigatória', status: 'warning', duration: 2500 }); return; }
        if (!contatoOk) { toast({ title: 'Contato obrigatório', status: 'warning', duration: 2500 }); return; }
        // Contato precisa estar cadastrado na lista (não aceita contato novo/texto livre)
        const namesToCheck = formCard.contatosSelecionados && formCard.contatosSelecionados.length > 0
            ? formCard.contatosSelecionados
            : formCard.contato ? formCard.contato.split(',').map(n => n.trim()) : [];

        const contatosNaoCadastrados = namesToCheck
            .filter(nome => nome && !(contatos || []).some(c => c.nome?.toLowerCase() === String(nome).trim().toLowerCase()));
        if (contatosNaoCadastrados.length > 0) {
            toast({ title: `Contato "${contatosNaoCadastrados[0]}" não está cadastrado`, description: 'Cadastre o contato em Contatos antes de vincular ao negócio.', status: 'warning', duration: 3500 });
            return;
        }
        if (!targetColunaId) { toast({ title: 'Selecione a etapa do funil', status: 'warning', duration: 2500 }); return; }
        if (!formCard.canal?.trim()) { toast({ title: 'Canal de origem obrigatório', status: 'warning', duration: 2500 }); return; }
        if (!formCard.dataInicio) { toast({ title: 'Data de início obrigatória', status: 'warning', duration: 2500 }); return; }
        if (!formCard.dataFechamento) { toast({ title: 'Data de fechamento obrigatória', status: 'warning', duration: 2500 }); return; }
        const hojeStr = new Date().toLocaleDateString('en-CA');
        if (formCard.dataFechamento < formCard.dataInicio) { toast({ title: 'A data de fechamento não pode ser anterior à de início', status: 'warning', duration: 2500 }); return; }
        if (!internalCardEditando && formCard.dataInicio < hojeStr) { toast({ title: 'A data de início não pode ser no passado', status: 'warning', duration: 2500 }); return; }
        // Responsável precisa estar cadastrado no banco (não pode ser texto livre/novo)
        if (!finalAssigneeId) {
            toast({ title: formCard.responsavel?.trim() ? 'Responsável precisa estar cadastrado no sistema' : 'Responsável obrigatório', status: 'warning', duration: 3000 });
            return;
        }

        // Empresa nova (externa, sem marca do ERP) → confirma antes de criar
        const empresaNova = !formCard.company_id && !formCard.marcaId;
        if (!internalCardEditando && empresaNova && !skipConfirmEmpresa) {
            onConfirmEmpresaOpen();
            return;
        }

        const payload = {
            ...formCard,
            // A empresa/marca selecionada vive em `marcaId` (id do ERP / FRANQUIA_MARCA_PROPRIA).
            // O banco espera em `company_id` — sem este mapeamento, id_company salvava NULL.
            company_id: formCard.marcaId || formCard.company_id || null,
            meetLink: reuniaoLink || formCard.meetLink || '',
            assignee_id: finalAssigneeId || null,
            valor: Number(formCard.valor) || 0,
            colunaId: targetColunaId,
            pipelineId: funilForm?.id,
            completed,
        };

        // Observações novas vivem exclusivamente na linha do tempo. Não reenviar
        // a coluna legada evita sobrescrever o histórico somente leitura.
        delete payload.observacoes;

        let cardSalvoId;
        if (internalCardEditando) {
            await updatePipelineCard(internalCardEditando.id, payload);
            cardSalvoId = internalCardEditando.id;
        } else {
            const newCard = await addPipelineCard(payload);
            cardSalvoId = newCard?.id;
            // Eventos da agenda (staging) → criam atividades de calendário vinculadas ao novo negócio.
            if (formCard.compromissos && formCard.compromissos.length > 0) {
                for (const comp of formCard.compromissos) {
                    try {
                        await addAtividade(compromissoParaAtividade(comp, newCard.id));
                    } catch (err) {
                        console.error("Erro ao criar evento do negócio:", err);
                    }
                }
            }
            // Veio do arraste de uma atividade → vincula ela ao negócio recém-criado.
            if (atividadeVinculoPendente && newCard?.id) {
                try {
                    await updateAtividade(atividadeVinculoPendente.id, { ...atividadeVinculoPendente, cardId: String(newCard.id) });
                } catch (err) {
                    console.error("Erro ao vincular atividade ao novo negócio:", err);
                }
            }

        }

        let anotacaoFalhou = false;
        if (novaAnotacao.trim()) {
            try {
                await criarAnotacao({ refType: 'card', refId: cardSalvoId, text: novaAnotacao });
            } catch (err) {
                anotacaoFalhou = true;
                console.error('Negócio salvo, mas a anotação não foi registrada:', err);
            }
        }

        // Feedback das transições de estado (regra: resultado ativo ⟺ concluído).
        const partesDescricao = [];
        if (internalCardEditando && !!internalCardEditando.completed && !completed) {
            partesDescricao.push('Resultado "Em aberto": o negócio voltou ao Kanban do Pipeline, na etapa escolhida.');
        } else if (internalCardEditando && !internalCardEditando.completed && completed) {
            partesDescricao.push('Negócio concluído: saiu do Kanban e passa a viver em Relatórios.');
        }
        if (novaAnotacao.trim()) {
            partesDescricao.push(anotacaoFalhou
                ? 'O negócio foi salvo, mas não foi possível registrar a nova observação.'
                : 'Nova observação adicionada à linha do tempo em Anotações.');
        }
        toast({
            title: internalCardEditando ? 'Negócio atualizado' : 'Negócio criado',
            description: partesDescricao.length > 0 ? partesDescricao.join(' ') : undefined,
            status: anotacaoFalhou ? 'warning' : 'success',
            duration: anotacaoFalhou ? 4500 : (partesDescricao.length > 0 ? 4000 : 2500),
            isClosable: true,
        });
        setAtividadeVinculoPendente(null);
        handleFecharCard();
    }, [formCard, novaAnotacao, cardColunaId, internalCardEditando, funilForm, addPipelineCard, updatePipelineCard, addAtividade, compromissoParaAtividade, handleFecharCard, toast, contatoNaoEncontrado, contatos, employees, atividadeVinculoPendente, updateAtividade, onConfirmEmpresaOpen]);

    // ── Modal: Coluna (criar) ─────────────────────────────────────
    const { isOpen: isColunaOpen, onOpen: onColunaOpen, onClose: onColunaClose } = useDisclosure();
    const [formColuna, setFormColuna] = useState({ label: '', cor: '#4299E1', tipo: 'aberto' });

    const handleSalvarColuna = useCallback(() => {
        if (!formColuna.label.trim()) {
            toast({ title: 'Nome obrigatório', status: 'warning', duration: 2000 });
            return;
        }
        addColuna(pipelineAtivo.id, formColuna);
        toast({ title: `Coluna "${formColuna.label}" criada`, status: 'success', duration: 2000 });
        setFormColuna({ label: '', cor: '#4299E1', tipo: 'aberto' });
        onColunaClose();
    }, [formColuna, pipelineAtivo, addColuna, onColunaClose, toast]);

    // ── Modal: Renomear coluna ────────────────────────────────────
    const { isOpen: isRenomearOpen, onOpen: onRenomearOpen, onClose: onRenomearClose } = useDisclosure();
    const [colunaRenomeando, setColunaRenomeando] = useState(null);
    const [formRenomear, setFormRenomear] = useState({ label: '', cor: '' });

    const handleAbrirRenomear = useCallback((coluna) => {
        setColunaRenomeando(coluna);
        setFormRenomear({ label: coluna.label, cor: coluna.cor, tipo: coluna.tipo || 'aberto' });
        onRenomearOpen();
    }, [onRenomearOpen]);

    const handleSalvarRenomear = useCallback(() => {
        if (!formRenomear.label.trim()) return;
        updateColuna(pipelineAtivo.id, colunaRenomeando.id, formRenomear);
        toast({ title: 'Coluna atualizada', status: 'success', duration: 2000 });
        onRenomearClose();
    }, [formRenomear, pipelineAtivo, colunaRenomeando, updateColuna, onRenomearClose, toast]);

    // ── Modal: Nova aba (pipeline) ────────────────────────────────
    const { isOpen: isAbaOpen, onOpen: onAbaOpen, onClose: onAbaClose } = useDisclosure();
    const [formAba, setFormAba] = useState({ nome: '', cor: '#4299E1' });

    const handleSalvarAba = useCallback(() => {
        if (!formAba.nome.trim()) {
            toast({ title: 'Nome obrigatório', status: 'warning', duration: 2000 });
            return;
        }
        const novo = addPipeline(formAba.nome, formAba.cor);
        handleSetPipelineAtivo(novo.id);
        toast({ title: `Pipeline "${formAba.nome}" criado`, status: 'success', duration: 2000 });
        setFormAba({ nome: '', cor: '#4299E1' });
        onAbaClose();
    }, [formAba, addPipeline, onAbaClose, toast]);

    // ── Renomear aba ─────────────────────────────────────────────
    const { isOpen: isRenomearAbaOpen, onOpen: onRenomearAbaOpen, onClose: onRenomearAbaClose } = useDisclosure();
    const [abaRenomeando, setAbaRenomeando] = useState(null);
    const [formRenomearAba, setFormRenomearAba] = useState({ nome: '', cor: '' });

    const handleAbrirRenomearAba = useCallback((pipeline, e) => {
        e.stopPropagation();
        setAbaRenomeando(pipeline);
        setFormRenomearAba({ nome: pipeline.nome, cor: pipeline.cor });
        onRenomearAbaOpen();
    }, [onRenomearAbaOpen]);

    const handleSalvarRenomearAba = useCallback(() => {
        if (!formRenomearAba.nome.trim()) return;
        updatePipeline(abaRenomeando.id, formRenomearAba);
        toast({ title: 'Pipeline renomeado', status: 'success', duration: 2000 });
        onRenomearAbaClose();
    }, [formRenomearAba, abaRenomeando, updatePipeline, onRenomearAbaClose, toast]);

    // ── Mover card entre colunas ──────────────────────────────────
    const handleMoverCard = useCallback((cardId, direcao) => {
        const card = pipelineCards.find(c => c.id === cardId);
        if (!card || !pipelineAtivo) return;
        const colunas = pipelineAtivo.colunas;
        const idx = colunas.findIndex(c => c.id === card.colunaId);
        const novoIdx = direcao === 'next' ? idx + 1 : idx - 1;
        if (novoIdx < 0 || novoIdx >= colunas.length) return;
        moverCard(cardId, colunas[novoIdx].id);
    }, [pipelineCards, pipelineAtivo, moverCard]);

    if (!pipelineAtivo) return null;

    const corProb = formCard.probabilidade >= 70 ? '#02EA75' : formCard.probabilidade >= 40 ? '#ECC94B' : '#FC8181';

    // Fundo do modal Novo/Editar Negócio segue o token `surface` do tema (mesmo
    // preto/branco dos demais modais, ex.: Nova Atividade) — sem vidro acinzentado.
    const modalGlassBg = colorMode === 'dark' ? '#161719' : '#FFFFFF';
    const modalGlassBorder = colorMode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 19, 0, 0.16)';
    const inputGlassBg = colorMode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 19, 0, 0.02)';
    const inputGlassFocusBg = colorMode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 19, 0, 0.03)';
    const inputGlassBorderHover = colorMode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 19, 0, 0.22)';

    return (
        <>
            <Modal isOpen={isOpen} onClose={handleMinimizarCard} isCentered size="4xl" scrollBehavior="inside" closeOnOverlayClick={true}>
                <ModalOverlay backdropFilter="blur(8px)" bg="rgba(0,0,0,0.4)" />
                <ModalContent
                    bg={modalGlassBg}
                    border="1px solid"
                    borderColor={modalGlassBorder}
                    color="textPrimary"
                    onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSalvarCard(); }}
                >
                    {/* Header */}
                    <ModalHeader borderBottom="1px solid" borderColor="borderPrimary" pb={4} pr="90px">
                        <HStack spacing={3}>
                            <Flex
                                w="32px" h="32px"
                                bg="surfaceHover"
                                borderRadius="lg"
                                align="center"
                                justify="center"
                                flexShrink={0}
                            >
                                <Icon as={AiOutlinePartition} color="secondary" boxSize="16px" />
                            </Flex>
                            <Box>
                                <Text fontWeight="800" fontSize="md" lineHeight="1.2">
                                    {internalCardEditando ? 'Editar Negócio' : 'Novo Negócio'}
                                </Text>
                                {cardColunaId && funilForm && (() => {
                                    const col = (funilForm.colunas || []).find(c => c.id === cardColunaId);
                                    return col ? (
                                        <HStack spacing={1.5} mt={0.5}>
                                            <Box w="6px" h="6px" borderRadius="full" bg={col.cor} />
                                            <Text fontSize="xs" color="textSecondary" fontWeight="600">{funilForm.nome} · {col.label}</Text>
                                        </HStack>
                                    ) : null;
                                })()}
                            </Box>
                            {internalCardEditando && formCard.valor > 0 && (
                                <Badge
                                    ml="auto"
                                    mr={2}
                                    bg="surfaceHover"
                                    color="secondary"
                                    fontSize="sm"
                                    fontWeight="900"
                                    px={3}
                                    py={1}
                                    borderRadius="lg"
                                >
                                    {fmt(formCard.valor)}
                                </Badge>
                            )}
                        </HStack>
                    </ModalHeader>

                    {/* Botões no topo: Minimizar e Fechar */}
                    <HStack position="absolute" top={4} right={4} spacing={1} zIndex={10}>
                        <IconButton
                            aria-label="Minimizar"
                            title="Minimizar"
                            icon={<Icon as={AiOutlineMinus} boxSize="16px" />}
                            size="sm"
                            variant="ghost"
                            color="textSecondary"
                            _hover={{ bg: "whiteAlpha.200", color: "secondary" }}
                            borderRadius="md"
                            onClick={handleMinimizarCard}
                        />
                        <ModalCloseButton position="relative" top={0} right={0} color="textSecondary" onClick={handleFecharCard} />
                    </HStack>

                    <ModalBody py={5}>
                        <VStack spacing={5} align="stretch">

                            {/* ── Seção: Negócio ── */}
                            <SecaoLabel label="Negócio" />
                            <FormControl isRequired>
                                <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                    Título do Negócio
                                </FormLabel>
                                <Input
                                    value={formCard.titulo}
                                    onChange={e => setFormCard(p => ({ ...p, titulo: e.target.value }))}
                                    placeholder="Ex: ERP + App Completo"
                                    bg={inputGlassBg}
                                    borderColor={modalGlassBorder}
                                    _hover={{ borderColor: inputGlassBorderHover }}
                                    _focus={{ bg: inputGlassFocusBg, borderColor: 'secondary', boxShadow: '0 0 0 1px var(--chakra-colors-secondary)' }}
                                    fontWeight="700"
                                    size="md"
                                    autoFocus
                                />
                            </FormControl>

                            {/* ── Seção: Contato ── */}
                            <SecaoLabel label="Contato" />
                            <HStack spacing={3} align="flex-start">
                                {/* Empresa com busca */}
                                <FormControl>
                                    <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Empresa
                                    </FormLabel>
                                    <Box position="relative" ref={empresaInputRef}>
                                        <InputGroup>
                                            <InputLeftElement pointerEvents="none">
                                                <Icon as={AiOutlineSearch} color="textMuted" boxSize="14px" />
                                            </InputLeftElement>
                                            <Input
                                                value={formCard.empresa}
                                                onChange={e => {
                                                    setFormCard(p => ({
                                                        ...p,
                                                        empresa: e.target.value,
                                                        // ao digitar, desfaz vínculo de marca (vai reidentificar)
                                                        marcaId: null,
                                                    }));
                                                    setEmpresaDropdownAberto(true);
                                                }}
                                                onFocus={() => setEmpresaDropdownAberto(true)}
                                                onBlur={() => setTimeout(() => setEmpresaDropdownAberto(false), 180)}
                                                placeholder="Buscar marca, empresa..."
                                                bg={inputGlassBg}
                                                borderColor={marcaVinculada ? '#4299E1' : (contatoSelecionado ? 'secondary' : (formCard.clienteNovo ? '#ED8936' : modalGlassBorder))}
                                                _hover={{ borderColor: marcaVinculada ? '#4299E1' : (contatoSelecionado ? 'secondary' : (formCard.clienteNovo ? '#ED8936' : inputGlassBorderHover)) }}
                                                _focus={{ bg: inputGlassFocusBg, borderColor: 'secondary', boxShadow: '0 0 0 1px var(--chakra-colors-secondary)' }}
                                                pl={9}
                                            />
                                        </InputGroup>

                                        {/* Badge de fonte (ERP / CRM / Novo) */}
                                        {(marcaVinculada || contatoSelecionado || formCard.clienteNovo) && !empresaDropdownAberto && (
                                            <Badge
                                                position="absolute"
                                                right={2}
                                                top="50%"
                                                transform="translateY(-50%)"
                                                fontSize="8px"
                                                fontWeight="900"
                                                textTransform="uppercase"
                                                letterSpacing="0.5px"
                                                px={1.5}
                                                py={0.5}
                                                borderRadius="md"
                                                bg={marcaVinculada ? '#4299E120' : (contatoSelecionado ? '#02EA7520' : '#ED893620')}
                                                color={marcaVinculada ? '#4299E1' : (contatoSelecionado ? '#02EA75' : '#ED8936')}
                                            >
                                                {marcaVinculada ? 'ERP' : (contatoSelecionado ? 'CRM' : 'Novo')}
                                            </Badge>
                                        )}

                                        {/* Dropdown de sugestões */}
                                        {empresaDropdownAberto && formCard.empresa && (
                                            <Box
                                                position="absolute"
                                                top="calc(100% + 4px)"
                                                left={0}
                                                right={0}
                                                zIndex={9999}
                                                bg="surface"
                                                border="1px solid"
                                                borderColor="borderPrimary"
                                                borderRadius="xl"
                                                overflow="hidden"
                                                boxShadow="0 12px 40px rgba(0,0,0,0.6)"
                                                maxH="320px"
                                                overflowY="auto"
                                            >
                                                {/* SEÇÃO: Marcas do ERP */}
                                                {marcasFiltradas.length > 0 && (
                                                    <>
                                                        <Box px={3} py={1.5} bg="surfaceHover">
                                                            <HStack spacing={1.5}>
                                                                <Icon as={AiOutlineDatabase} color="#4299E1" boxSize="10px" />
                                                                <Text fontSize="9px" color="#4299E1" fontWeight="900" letterSpacing="1.5px" textTransform="uppercase">
                                                                    Cadastro ERP · {marcasFiltradas.length}
                                                                </Text>
                                                            </HStack>
                                                        </Box>
                                                        {marcasFiltradas.map(m => (
                                                            <Box
                                                                key={`marca-${m.id}`}
                                                                px={3}
                                                                py={2.5}
                                                                cursor="pointer"
                                                                _hover={{ bg: 'surfaceHover' }}
                                                                onMouseDown={() => handleSelecionarMarca(m)}
                                                                borderBottom="1px solid"
                                                                borderColor="borderPrimary"
                                                            >
                                                                <HStack spacing={2.5}>
                                                                    <Avatar
                                                                        size="xs"
                                                                        name={m.nome}
                                                                        src={m.logo}
                                                                        bg={m.cor_principal || 'gray.600'}
                                                                        color="textPrimary"
                                                                        fontSize="9px"
                                                                        fontWeight="900"
                                                                        sx={{
                                                                            '& img': { objectFit: 'contain', p: '2px' },
                                                                        }}
                                                                    />
                                                                    <Box flex={1} minW={0}>
                                                                        <Text fontSize="sm" color="textPrimary" fontWeight="700" lineHeight="1.2" isTruncated>
                                                                            {m.nome}
                                                                        </Text>
                                                                        <Text fontSize="xs" color="textMuted" isTruncated>
                                                                            {m.provedor ? m.provedor : 'Sem ERP'}
                                                                        </Text>
                                                                    </Box>
                                                                    <Badge fontSize="8px" bg="#4299E120" color="#4299E1" fontWeight="900" px={1.5}>
                                                                        ERP
                                                                    </Badge>
                                                                </HStack>
                                                            </Box>
                                                        ))}
                                                    </>
                                                )}

                                                {/* SEÇÃO: Contatos do CRM */}
                                                {contatosFiltrados.length > 0 && (
                                                    <>
                                                        <Box px={3} py={1.5} bg="surfaceHover">
                                                            <HStack spacing={1.5}>
                                                                <Icon as={AiOutlineContacts} color="secondary" boxSize="10px" />
                                                                <Text fontSize="9px" color="secondary" fontWeight="900" letterSpacing="1.5px" textTransform="uppercase">
                                                                    Contatos CRM · {contatosFiltrados.length}
                                                                </Text>
                                                            </HStack>
                                                        </Box>
                                                        {contatosFiltrados.map(c => (
                                                            <Box
                                                                key={`cont-${c.id}`}
                                                                px={3}
                                                                py={2.5}
                                                                cursor="pointer"
                                                                _hover={{ bg: 'surfaceHover' }}
                                                                onMouseDown={() => handleSelecionarContato(c)}
                                                                borderBottom="1px solid"
                                                                borderColor="borderPrimary"
                                                            >
                                                                <HStack spacing={2.5}>
                                                                    <Avatar
                                                                        size="xs"
                                                                        name={c.empresa}
                                                                        bg="secondary"
                                                                        color="primary"
                                                                        fontSize="9px"
                                                                        fontWeight="900"
                                                                    />
                                                                    <Box flex={1} minW={0}>
                                                                        <Text fontSize="sm" color="textPrimary" fontWeight="700" lineHeight="1.2" isTruncated>
                                                                            {c.empresa}
                                                                        </Text>
                                                                        <Text fontSize="xs" color="textMuted" isTruncated>
                                                                            {c.nome}{c.cargo ? ` · ${c.cargo}` : ''}
                                                                        </Text>
                                                                    </Box>
                                                                    <Badge fontSize="8px" bg="#02EA7520" color="secondary" fontWeight="900" px={1.5}>
                                                                        CRM
                                                                    </Badge>
                                                                </HStack>
                                                            </Box>
                                                        ))}
                                                    </>
                                                )}

                                                {/* SEÇÃO: Fallback — cliente não cadastrado */}
                                                <Box
                                                    px={3}
                                                    py={2.5}
                                                    cursor="pointer"
                                                    _hover={{ bg: 'surfaceHover' }}
                                                    onMouseDown={handleUsarClienteNovo}
                                                    bg={temResultados ? 'transparent' : 'surfaceRaised'}
                                                    borderTop={temResultados ? '2px solid' : 'none'}
                                                    borderColor="borderPrimary"
                                                >
                                                    <HStack spacing={2.5}>
                                                        <Flex
                                                            w="24px" h="24px"
                                                            bg="#ED893620"
                                                            borderRadius="full"
                                                            align="center"
                                                            justify="center"
                                                            flexShrink={0}
                                                        >
                                                            <Icon as={AiOutlineUserAdd} color="#ED8936" boxSize="12px" />
                                                        </Flex>
                                                        <Box flex={1} minW={0}>
                                                            <Text fontSize="sm" color="textPrimary" fontWeight="700" lineHeight="1.2">
                                                                Usar "{formCard.empresa}" como cliente novo
                                                            </Text>
                                                            <Text fontSize="xs" color="textMuted">
                                                                {temResultados
                                                                    ? 'Não está nas opções acima'
                                                                    : 'Não encontrado no ERP nem no CRM'}
                                                            </Text>
                                                        </Box>
                                                        <Badge fontSize="8px" bg="#ED893620" color="#ED8936" fontWeight="900" px={1.5}>
                                                            Novo
                                                        </Badge>
                                                    </HStack>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                </FormControl>

                                {/* Contato com busca */}
                                <FormControl>
                                    <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Contato
                                    </FormLabel>
                                    <Box position="relative">
                                        <InputGroup>
                                            <InputLeftElement pointerEvents="none">
                                                <Icon as={AiOutlineSearch} color="textMuted" boxSize="14px" />
                                            </InputLeftElement>
                                            <Input
                                                value={formCard.contato}
                                                onChange={e => {
                                                    setFormCard(p => ({ ...p, contato: e.target.value }));
                                                    setBuscaContato(e.target.value);
                                                    setContatoFocado(true);
                                                }}
                                                onFocus={() => setContatoFocado(true)}
                                                onBlur={() => setTimeout(() => setContatoFocado(false), 180)}
                                                placeholder="Pesquisar ou digite e aperte Enter"
                                                bg={inputGlassBg}
                                                borderColor={modalGlassBorder}
                                                _hover={{ borderColor: inputGlassBorderHover }}
                                                _focus={{ bg: inputGlassFocusBg, borderColor: 'secondary', boxShadow: '0 0 0 1px var(--chakra-colors-secondary)' }}
                                                pl={9}
                                            />
                                        </InputGroup>

                                        {contatoFocado && formCard.contato && (
                                            <Box
                                                position="absolute"
                                                top="calc(100% + 4px)"
                                                left={0}
                                                right={0}
                                                zIndex={9999}
                                                bg="surface"
                                                border="1px solid"
                                                borderColor="borderPrimary"
                                                borderRadius="xl"
                                                overflow="hidden"
                                                boxShadow="0 12px 40px rgba(0,0,0,0.6)"
                                                maxH="240px"
                                                overflowY="auto"
                                            >
                                                {contatosSugeridos.length > 0 ? (
                                                    contatosSugeridos.map(c => (
                                                        <Box
                                                            key={`sug-cont-${c.id}`}
                                                            px={3}
                                                            py={2.5}
                                                            cursor="pointer"
                                                            _hover={{ bg: 'surfaceHover' }}
                                                            onMouseDown={() => handleSelecionarContatoCard(c)}
                                                            borderBottom="1px solid"
                                                            borderColor="borderPrimary"
                                                        >
                                                            <Text fontSize="sm" color="textPrimary" fontWeight="700">
                                                                {c.nome}
                                                            </Text>
                                                            {(c.cargo || c.telefone) && (
                                                                <Text fontSize="xs" color="textMuted">
                                                                    {c.cargo}{c.cargo && c.empresa ? ' · ' : ''}{c.empresa}{c.telefone ? ` · ${c.telefone}` : ''}
                                                                </Text>
                                                            )}
                                                        </Box>
                                                    ))
                                                ) : (
                                                    <Box px={3} py={2.5}>
                                                        <Text fontSize="sm" color="textMuted">Nenhum contato encontrado.</Text>
                                                        <Text fontSize="xs" color="textSecondary" mt={1}>Pressione Enter para usar "{formCard.contato}" de forma livre.</Text>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                </FormControl>
                            </HStack>

                            {/* Card de info da marca vinculada */}
                            {marcaVinculada && (
                                <Box
                                    bg="#4299E10c"
                                    border="1px solid"
                                    borderColor="#4299E120"
                                    borderRadius="xl"
                                    p={4}
                                    mt={2}
                                >
                                    <HStack spacing={3} align="flex-start">
                                        <Avatar
                                            size="sm"
                                            name={marcaVinculada.nome}
                                            src={marcaVinculada.logo}
                                            bg={marcaVinculada.cor_principal || '#4299E1'}
                                            color="white"
                                            fontWeight="900"
                                            fontSize="11px"
                                            flexShrink={0}
                                        />
                                        <Box flex={1} minW={0}>
                                            <HStack spacing={2} align="center">
                                                <Text fontSize="sm" color="textPrimary" fontWeight="800" lineHeight="1.2" isTruncated>
                                                    {marcaVinculada.nome}
                                                </Text>
                                                <Badge bg="#4299E120" color="#4299E1" fontSize="8px" px={1.5} borderRadius="sm">
                                                    ERP
                                                </Badge>
                                            </HStack>
                                            <Text fontSize="xs" color="textSecondary" mt={0.5}>
                                                {marcaVinculada.provedor || 'Sem ERP definido'}
                                                {marcaVinculada.data_cadastro
                                                    ? ` · Cadastro ${new Date(marcaVinculada.data_cadastro).toLocaleDateString('pt-BR')}`
                                                    : ''}
                                            </Text>
                                            <HStack spacing={4} mt={1.5} flexWrap="wrap">
                                                {marcaVinculada.responsavel_marca_nome && (
                                                    <HStack spacing={1.5}>
                                                        <Icon as={AiOutlineUser} color="textMuted" boxSize="11px" />
                                                        <Text fontSize="xs" color="textSecondary">{marcaVinculada.responsavel_marca_nome}</Text>
                                                    </HStack>
                                                )}
                                                {marcaVinculada.whatsapp && (
                                                    <HStack spacing={1.5}>
                                                        <Icon as={AiOutlinePhone} color="textMuted" boxSize="11px" />
                                                        <Text fontSize="xs" color="textSecondary">{marcaVinculada.whatsapp}</Text>
                                                    </HStack>
                                                )}
                                                {marcaVinculada.sac && (
                                                    <HStack spacing={1.5}>
                                                        <Icon as={AiOutlineMail} color="textMuted" boxSize="11px" />
                                                        <Text fontSize="xs" color="textSecondary">{marcaVinculada.sac}</Text>
                                                    </HStack>
                                                )}
                                            </HStack>
                                        </Box>
                                    </HStack>
                                </Box>
                            )}

                            {/* Aviso de cliente novo (não cadastrado) */}
                            {formCard.clienteNovo && !marcaVinculada && (
                                <Box
                                    bg="#ED89360c"
                                    border="1px solid"
                                    borderColor="#ED893620"
                                    borderRadius="xl"
                                    p={4}
                                    mt={2}
                                >
                                    <HStack spacing={3} align="center">
                                        <Flex
                                            w="32px" h="32px"
                                            bg="#ED893620"
                                            borderRadius="full"
                                            align="center"
                                            justify="center"
                                            flexShrink={0}
                                            mb="auto"
                                        >
                                            <Icon as={AiOutlineWarning} color="#ED8936" boxSize="14px" />
                                        </Flex>
                                        <Box flex={1}>
                                            <Text fontSize="sm" color="textPrimary" fontWeight="800" lineHeight="1.2">
                                                Cliente novo · sem cadastro
                                            </Text>
                                            <Text fontSize="xs" color="textSecondary" mt={0.5}>
                                                Após fechar o negócio, considere cadastrar como Contato ou Marca no ERP.
                                            </Text>
                                        </Box>
                                    </HStack>
                                </Box>
                            )}

                            {/* Card de info do contato selecionado */}
                            {!marcaVinculada && contatoSelecionado && (
                                <Box
                                    bg="#02EA7507"
                                    border="1px solid"
                                    borderColor="#02EA7522"
                                    borderRadius="xl"
                                    p={4}
                                    mt={2}
                                >
                                    <HStack spacing={3} align="flex-start">
                                        <Avatar
                                            size="sm"
                                            name={contatoSelecionado.nome}
                                            bg="secondary"
                                            color="primary"
                                            fontWeight="900"
                                            fontSize="11px"
                                            flexShrink={0}
                                        />
                                        <Box flex={1} minW={0}>
                                            <Text fontSize="sm" color="textPrimary" fontWeight="800" lineHeight="1.2">
                                                {contatoSelecionado.nome}
                                            </Text>
                                            {contatoSelecionado.cargo && (
                                                <Text fontSize="xs" color="textSecondary" mt={0.5}>
                                                    {contatoSelecionado.cargo} · {contatoSelecionado.empresa}
                                                </Text>
                                            )}
                                            <HStack spacing={4} mt={1.5} flexWrap="wrap">
                                                {contatoSelecionado.telefone && (
                                                    <HStack spacing={1.5}>
                                                        <Icon as={AiOutlinePhone} color="textMuted" boxSize="11px" />
                                                        <Text fontSize="xs" color="textSecondary">{contatoSelecionado.telefone}</Text>
                                                    </HStack>
                                                )}
                                                {contatoSelecionado.email && (
                                                    <HStack spacing={1.5}>
                                                        <Icon as={AiOutlineMail} color="textMuted" boxSize="11px" />
                                                        <Text fontSize="xs" color="textSecondary">{contatoSelecionado.email}</Text>
                                                    </HStack>
                                                )}
                                            </HStack>
                                        </Box>
                                        <Box flexShrink={0}>
                                            <Badge
                                                bg="surfaceHover"
                                                color="textSecondary"
                                                fontSize="9px"
                                                fontWeight="700"
                                                textTransform="uppercase"
                                                letterSpacing="0.5px"
                                                px={2}
                                                py={1}
                                                borderRadius="md"
                                            >
                                                {contatoSelecionado.canal}
                                            </Badge>
                                        </Box>
                                    </HStack>
                                </Box>
                            )}

                            {/* ── Seção: Financeiro ── */}
                            <SecaoLabel label="Financeiro" />
                            <HStack spacing={3} align="flex-start">
                                <FormControl>
                                    <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Valor (R$)
                                    </FormLabel>
                                    <NumberInput
                                        value={formCard.valor}
                                        onChange={val => setFormCard(p => ({ ...p, valor: val }))}
                                        min={0}
                                    >
                                        <NumberInputField
                                            placeholder="0"
                                            bg={inputGlassBg}
                                            borderColor={modalGlassBorder}
                                            _hover={{ borderColor: inputGlassBorderHover }}
                                            _focus={{ bg: inputGlassFocusBg, borderColor: 'secondary', boxShadow: '0 0 0 1px var(--chakra-colors-secondary)' }}
                                        />
                                    </NumberInput>
                                    {Number(formCard.valor) > 0 && (
                                        <Text fontSize="11px" color="secondary" fontWeight="800" mt={1} pl={1}>
                                            {fmt(formCard.valor)}
                                        </Text>
                                    )}
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Probabilidade (%)
                                    </FormLabel>
                                    <NumberInput
                                        value={formCard.probabilidade}
                                        onChange={val => setFormCard(p => ({ ...p, probabilidade: Number(val) }))}
                                        min={0} max={100}
                                    >
                                        <NumberInputField
                                            bg={inputGlassBg}
                                            borderColor={modalGlassBorder}
                                            _hover={{ borderColor: inputGlassBorderHover }}
                                            _focus={{ bg: inputGlassFocusBg, borderColor: 'secondary', boxShadow: '0 0 0 1px var(--chakra-colors-secondary)' }}
                                        />
                                    </NumberInput>
                                    <Box mt={1.5} px={0.5}>
                                        <Progress
                                            value={formCard.probabilidade || 0}
                                            size="xs"
                                            bg="surfaceHover"
                                            borderRadius="full"
                                            sx={{
                                                '& > div': {
                                                    background: corProb,
                                                    transition: 'width 0.3s ease, background 0.3s ease',
                                                }
                                            }}
                                        />
                                    </Box>
                                </FormControl>
                            </HStack>

                            {/* ── Seção: Pipeline ── */}
                            <SecaoLabel label="Pipeline" />
                            <HStack spacing={3} align="flex-start">
                                <FormControl>
                                    <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Funil
                                    </FormLabel>
                                    <SearchableSelect
                                        value={funilForm?.id || ''}
                                        onChange={v => {
                                            const novoFunil = (pipelines || []).find(p => String(p.id) === String(v));
                                            if (!novoFunil) return;
                                            setFunilFormId(novoFunil.id);
                                            // Voltou ao funil original do card → restaura a etapa original;
                                            // funil diferente → primeira etapa (por ordem) do funil novo.
                                            if (internalCardEditando && String(internalCardEditando.pipelineId) === String(novoFunil.id)) {
                                                setCardColunaId(internalCardEditando.colunaId);
                                            } else {
                                                const colsNovoFunil = [...(novoFunil.colunas || [])].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
                                                setCardColunaId(colsNovoFunil[0]?.id || '');
                                            }
                                        }}
                                        options={(pipelines || []).map(p => ({ value: p.id, label: p.nome }))}
                                        placeholder="Buscar funil..."
                                        bg={inputGlassBg}
                                        borderColor={modalGlassBorder}
                                        _hover={{ borderColor: inputGlassBorderHover }}
                                        _focus={{ bg: inputGlassFocusBg, borderColor: 'secondary', boxShadow: '0 0 0 1px var(--chakra-colors-secondary)' }}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Etapa / Coluna
                                    </FormLabel>
                                    <SearchableSelect
                                        value={cardColunaId}
                                        onChange={v => setCardColunaId(v)}
                                        options={colunasFormulario.map(c => ({ value: c.id, label: c.label }))}
                                        placeholder="Buscar etapa..."
                                        bg={inputGlassBg}
                                        borderColor={modalGlassBorder}
                                        _hover={{ borderColor: inputGlassBorderHover }}
                                        _focus={{ bg: inputGlassFocusBg, borderColor: 'secondary', boxShadow: '0 0 0 1px var(--chakra-colors-secondary)' }}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Prioridade
                                    </FormLabel>
                                    <HStack spacing={2} mt={1}>
                                        {PRIORIDADES.map(p => (
                                            <Box
                                                key={p.id}
                                                px={3} py={1.5}
                                                borderRadius="lg"
                                                cursor="pointer"
                                                bg={formCard.prioridade === p.id ? `${p.cor}25` : 'surfaceRaised'}
                                                border="1px solid"
                                                borderColor={formCard.prioridade === p.id ? p.cor : 'borderPrimary'}
                                                color={formCard.prioridade === p.id ? p.cor : 'textMuted'}
                                                fontSize="xs"
                                                fontWeight="700"
                                                onClick={() => setFormCard(prev => ({ ...prev, prioridade: p.id }))}
                                                transition="all 0.15s"
                                                _hover={{ borderColor: p.cor, color: p.cor }}
                                                flex={1}
                                                textAlign="center"
                                            >
                                                {p.id}
                                            </Box>
                                        ))}
                                    </HStack>
                                </FormControl>
                            </HStack>

                            {/* ── Seção: Detalhes ── */}
                            <SecaoLabel label="Detalhes" />
                            <SimpleGrid columns={4} spacing={3}>
                                <FormControl>
                                    <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Canal de Origem
                                    </FormLabel>
                                    <SearchableSelect
                                        value={formCard.canal}
                                        onChange={v => setFormCard(p => ({ ...p, canal: v || '' }))}
                                        options={opcoesCanal}
                                        placeholder="Buscar canal..."
                                        bg={inputGlassBg}
                                        borderColor={modalGlassBorder}
                                        _hover={{ borderColor: inputGlassBorderHover }}
                                        _focus={{ bg: inputGlassFocusBg, borderColor: 'secondary', boxShadow: '0 0 0 1px var(--chakra-colors-secondary)' }}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Data de Início
                                    </FormLabel>
                                    <Input
                                        type="date"
                                        value={formCard.dataInicio}
                                        onChange={e => setFormCard(p => ({ ...p, dataInicio: e.target.value }))}
                                        bg={inputGlassBg}
                                        borderColor={modalGlassBorder}
                                        _hover={{ borderColor: inputGlassBorderHover }}
                                        _focus={{ bg: inputGlassFocusBg, borderColor: 'secondary', boxShadow: '0 0 0 1px var(--chakra-colors-secondary)' }}
                                        sx={{ colorScheme: 'dark' }}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Previsão de Fechamento
                                    </FormLabel>
                                    <Input
                                        type="date"
                                        value={formCard.dataFechamento}
                                        onChange={e => setFormCard(p => ({ ...p, dataFechamento: e.target.value }))}
                                        bg={inputGlassBg}
                                        borderColor={modalGlassBorder}
                                        _hover={{ borderColor: inputGlassBorderHover }}
                                        _focus={{ bg: inputGlassFocusBg, borderColor: 'secondary', boxShadow: '0 0 0 1px var(--chakra-colors-secondary)' }}
                                        sx={{ colorScheme: 'dark' }}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Resultado
                                    </FormLabel>
                                    <SearchableSelect
                                        value={formCard.result_id || ''}
                                        onChange={v => setFormCard(p => ({ ...p, result_id: v ? Number(v) : null, completed: !!v }))}
                                        options={(resultadosNegocio || []).map(r => ({ value: r.id, label: r.nome }))}
                                        isClearable
                                        placeholder="Em aberto"
                                        bg={inputGlassBg}
                                        borderColor={modalGlassBorder}
                                        _hover={{ borderColor: inputGlassBorderHover }}
                                        _focus={{ bg: inputGlassFocusBg, borderColor: 'secondary', boxShadow: '0 0 0 1px var(--chakra-colors-secondary)' }}
                                    />
                                </FormControl>
                            </SimpleGrid>

                            <FormControl position="relative">
                                <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                    Responsável
                                </FormLabel>
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none">
                                        <Icon as={AiOutlineIdcard} color="textMuted" boxSize="14px" />
                                    </InputLeftElement>
                                    <Input
                                        value={formCard.responsavel}
                                        onChange={e => setFormCard(p => ({ ...p, responsavel: e.target.value }))}
                                        onFocus={() => setResponsavelFocado(true)}
                                        onBlur={() => setTimeout(() => setResponsavelFocado(false), 200)}
                                        placeholder="Nome do responsável"
                                        bg={inputGlassBg}
                                        borderColor={modalGlassBorder}
                                        _hover={{ borderColor: inputGlassBorderHover }}
                                        _focus={{ bg: inputGlassFocusBg, borderColor: 'secondary', boxShadow: '0 0 0 1px var(--chakra-colors-secondary)' }}
                                        pl={9}
                                    />
                                </InputGroup>
                                {responsavelFocado && responsaveisSugeridos.length > 0 && (
                                    <Box
                                        position="absolute"
                                        top="100%"
                                        left={0}
                                        right={0}
                                        bg="surfaceRaised"
                                        border="1px solid"
                                        borderColor="borderPrimary"
                                        borderRadius="md"
                                        zIndex={10000}
                                        maxH="200px"
                                        overflowY="auto"
                                        boxShadow="0 4px 12px rgba(0,0,0,0.5)"
                                    >
                                        {responsaveisSugeridos.map(emp => (
                                            <Box
                                                key={emp.id}
                                                px={3}
                                                py={2}
                                                cursor="pointer"
                                                _hover={{ bg: 'surfaceHover', color: 'secondary' }}
                                                onMouseDown={() => handleSelecionarResponsavelCard(emp)}
                                                fontSize="sm"
                                                color="textPrimary"
                                            >
                                                {emp.name}
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </FormControl>

                            {/* ── Seção: Agenda / Compromissos ── */}
                            <SecaoLabel label="Agenda · Compromissos" />
                            <VStack align="stretch" spacing={2}>
                                {/* Lista de compromissos existentes */}
                                {(formCard.compromissos || []).length === 0 && !novoCompromissoAberto && (
                                    <Box
                                        bg="surfaceRaised"
                                        border="1px dashed"
                                        borderColor="borderPrimary"
                                        borderRadius="lg"
                                        py={3}
                                        textAlign="center"
                                    >
                                        <Text fontSize="xs" color="textMuted">
                                            Nenhum compromisso agendado
                                        </Text>
                                    </Box>
                                )}

                                {(formCard.compromissos || []).map(comp => {
                                    const tipoMeta = TIPOS_COMPROMISSO.find(t => t.id === comp.tipo) || TIPOS_COMPROMISSO[0];
                                    return (
                                        <Box
                                            key={comp.id}
                                            bg="surfaceHover"
                                            border="1px solid"
                                            borderColor={comp.concluido ? 'gray.700' : `${tipoMeta.cor}40`}
                                            borderRadius="lg"
                                            p={2.5}
                                            position="relative"
                                            opacity={comp.concluido ? 0.6 : 1}
                                            transition="all 0.15s"
                                            _hover={{ borderColor: tipoMeta.cor }}
                                            cursor="pointer"
                                            onClick={() => handleEditarCompromisso(comp)}
                                        >
                                            <HStack spacing={2.5} align="flex-start">
                                                {/* Checkbox de concluído */}
                                                <Box
                                                    as="button"
                                                    w="18px" h="18px"
                                                    borderRadius="md"
                                                    border="2px solid"
                                                    borderColor={comp.concluido ? tipoMeta.cor : 'gray.600'}
                                                    bg={comp.concluido ? tipoMeta.cor : 'transparent'}
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    onClick={(e) => { e.stopPropagation(); handleToggleCompromissoConcluido(comp.id); }}
                                                    flexShrink={0}
                                                    mt={0.5}
                                                    _hover={{ borderColor: tipoMeta.cor }}
                                                    transition="all 0.15s"
                                                >
                                                    {comp.concluido && (
                                                        <Icon as={AiOutlineCheck} color="textPrimary" boxSize="10px" />
                                                    )}
                                                </Box>

                                                <Box flex={1} minW={0}>
                                                    <HStack spacing={1.5} mb={0.5}>
                                                        <Badge
                                                            fontSize="8px"
                                                            bg={`${tipoMeta.cor}20`}
                                                            color={tipoMeta.cor}
                                                            fontWeight="900"
                                                            px={1.5}
                                                            borderRadius="sm"
                                                            textTransform="uppercase"
                                                        >
                                                            {comp.tipo}
                                                        </Badge>
                                                        <Text
                                                            fontSize="xs"
                                                            color="textPrimary"
                                                            fontWeight="800"
                                                            textDecoration={comp.concluido ? 'line-through' : 'none'}
                                                            isTruncated
                                                            flex={1}
                                                        >
                                                            {comp.titulo}
                                                        </Text>
                                                    </HStack>
                                                    <HStack spacing={2.5} fontSize="10px" color="textMuted" flexWrap="wrap">
                                                        <HStack spacing={1}>
                                                            <Icon as={AiOutlineCalendar} boxSize="10px" />
                                                            <Text fontWeight="700">
                                                                {new Date(comp.data + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                                                            </Text>
                                                        </HStack>
                                                        {comp.dataFim && comp.dataFim !== comp.data && (
                                                            <Text fontWeight="700">→ {new Date(comp.dataFim + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })}</Text>
                                                        )}
                                                        {(comp.horaInicio || comp.hora) && (
                                                            <Text fontWeight="700">· {comp.horaInicio || comp.hora}{comp.horaFim ? `–${comp.horaFim}` : ''}</Text>
                                                        )}
                                                        {comp.local && (
                                                            <Text isTruncated>· {comp.local}</Text>
                                                        )}
                                                        {comp.responsavel && (
                                                            <Text isTruncated>· Responsável: {comp.responsavel}</Text>
                                                        )}
                                                    </HStack>
                                                    {comp.descricao && (
                                                        <Text fontSize="11px" color="textSecondary" mt={1} noOfLines={2}>
                                                            {comp.descricao}
                                                        </Text>
                                                    )}
                                                </Box>

                                                <IconButton
                                                    aria-label="Editar"
                                                    icon={<AiOutlineEdit />}
                                                    size="xs"
                                                    variant="ghost"
                                                    color="textMuted"
                                                    _hover={{ color: 'secondary', bg: 'rgba(2,234,117,0.1)' }}
                                                    onClick={(e) => { e.stopPropagation(); handleEditarCompromisso(comp); }}
                                                    minW="22px" h="22px"
                                                />

                                                <IconButton
                                                    aria-label="Remover"
                                                    icon={<AiOutlineDelete />}
                                                    size="xs"
                                                    variant="ghost"
                                                    color="textMuted"
                                                    _hover={{ color: 'red.400', bg: 'rgba(229,62,62,0.1)' }}
                                                    onClick={(e) => { e.stopPropagation(); setCompRemovendo(comp); onDelCompOpen(); }}
                                                    minW="22px" h="22px"
                                                />
                                            </HStack>
                                        </Box>
                                    );
                                })}

                                {/* Formulário inline de novo compromisso */}
                                {novoCompromissoAberto ? (
                                    <Box
                                        bg="surfaceHover"
                                        border="1px solid"
                                        borderColor="secondary"
                                        borderRadius="lg"
                                        p={3}
                                    >
                                        <VStack align="stretch" spacing={2}>
                                            {/* Tipo + Título */}
                                            <HStack spacing={2}>
                                                <SearchableSelect
                                                    size="sm"
                                                    value={novoCompromisso.tipo}
                                                    onChange={v => setNovoCompromisso(p => ({ ...p, tipo: v || TIPOS_COMPROMISSO[0].id }))}
                                                    options={TIPOS_COMPROMISSO.map(t => ({ value: t.id, label: t.id }))}
                                                    placeholder="Tipo"
                                                    bg="surfaceHover"
                                                    borderColor="borderPrimary"
                                                    _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                                    maxW="120px"
                                                    fontSize="xs"
                                                    fontWeight="700"
                                                />
                                                <Input
                                                    size="sm"
                                                    value={novoCompromisso.titulo}
                                                    onChange={e => setNovoCompromisso(p => ({ ...p, titulo: e.target.value }))}
                                                    placeholder="Ex: Reunião com Roberto"
                                                    bg="surfaceHover"
                                                    borderColor="borderPrimary"
                                                    _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                                    fontWeight="700"
                                                    flex={1}
                                                    autoFocus
                                                />
                                            </HStack>

                                            {/* Data início + Data fim */}
                                            <HStack spacing={2} align="flex-end">
                                                <Box flex={1}>
                                                    <Text fontSize="9px" color="textMuted" fontWeight="800" textTransform="uppercase" letterSpacing="0.5px" mb={0.5}>Início</Text>
                                                    <Input
                                                        size="sm"
                                                        type="date"
                                                        value={novoCompromisso.data}
                                                        min={new Date().toLocaleDateString('en-CA')}
                                                        onChange={e => setNovoCompromisso(p => ({ ...p, data: e.target.value }))}
                                                        bg="surfaceHover"
                                                        borderColor="borderPrimary"
                                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                                        fontSize="xs"
                                                        sx={{ colorScheme: 'dark' }}
                                                    />
                                                </Box>
                                                <Box flex={1}>
                                                    <Text fontSize="9px" color="textMuted" fontWeight="800" textTransform="uppercase" letterSpacing="0.5px" mb={0.5}>Fim</Text>
                                                    <Input
                                                        size="sm"
                                                        type="date"
                                                        value={novoCompromisso.dataFim}
                                                        min={novoCompromisso.data || new Date().toLocaleDateString('en-CA')}
                                                        onChange={e => setNovoCompromisso(p => ({ ...p, dataFim: e.target.value }))}
                                                        bg="surfaceHover"
                                                        borderColor="borderPrimary"
                                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                                        fontSize="xs"
                                                        sx={{ colorScheme: 'dark' }}
                                                    />
                                                </Box>
                                            </HStack>

                                            {/* Hora início + Hora fim */}
                                            <HStack spacing={2} align="flex-end">
                                                <Box flex={1}>
                                                    <Text fontSize="9px" color="textMuted" fontWeight="800" textTransform="uppercase" letterSpacing="0.5px" mb={0.5}>Hora início</Text>
                                                    <Input
                                                        size="sm"
                                                        type="time"
                                                        value={novoCompromisso.horaInicio}
                                                        onChange={e => setNovoCompromisso(p => ({ ...p, horaInicio: e.target.value }))}
                                                        bg="surfaceHover"
                                                        borderColor="borderPrimary"
                                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                                        fontSize="xs"
                                                        sx={{ colorScheme: 'dark' }}
                                                    />
                                                </Box>
                                                <Box flex={1}>
                                                    <Text fontSize="9px" color="textMuted" fontWeight="800" textTransform="uppercase" letterSpacing="0.5px" mb={0.5}>Hora fim</Text>
                                                    <Input
                                                        size="sm"
                                                        type="time"
                                                        value={novoCompromisso.horaFim}
                                                        onChange={e => setNovoCompromisso(p => ({ ...p, horaFim: e.target.value }))}
                                                        bg="surfaceHover"
                                                        borderColor="borderPrimary"
                                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                                        fontSize="xs"
                                                        sx={{ colorScheme: 'dark' }}
                                                    />
                                                </Box>
                                            </HStack>

                                            {/* Local + Responsável */}
                                            <HStack spacing={2}>
                                                <Input
                                                    size="sm"
                                                    value={novoCompromisso.local}
                                                    onChange={e => setNovoCompromisso(p => ({ ...p, local: e.target.value }))}
                                                    placeholder="Local ou link (ex: Sala 1, Google Meet...)"
                                                    bg="surfaceHover"
                                                    borderColor="borderPrimary"
                                                    _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                                    fontSize="xs"
                                                    flex={1}
                                                />
                                                <SearchableSelect
                                                    size="sm"
                                                    value={novoCompromisso.employee_id || ''}
                                                    onChange={v => setNovoCompromisso(p => ({ ...p, employee_id: v || '' }))}
                                                    options={employees.map(emp => ({ value: emp.id, label: emp.name }))}
                                                    placeholder="Responsável"
                                                    isClearable
                                                    bg="surfaceHover"
                                                    borderColor="borderPrimary"
                                                    _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                                    maxW="160px"
                                                    fontSize="xs"
                                                />
                                            </HStack>

                                            {/* Descrição */}
                                            <Textarea
                                                size="sm"
                                                value={novoCompromisso.descricao}
                                                onChange={e => setNovoCompromisso(p => ({ ...p, descricao: e.target.value }))}
                                                placeholder="Detalhes / pauta (opcional)"
                                                bg="surfaceHover"
                                                borderColor="borderPrimary"
                                                _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                                fontSize="xs"
                                                rows={2}
                                                resize="vertical"
                                            />

                                            {/* Ações */}
                                            <HStack spacing={2} justify="flex-end">
                                                <Button
                                                    size="xs"
                                                    variant="ghost"
                                                    color="textSecondary"
                                                    onClick={() => {
                                                        setNovoCompromissoAberto(false);
                                                        setNovoCompromisso(COMPROMISSO_INICIAL);
                                                        setEditandoCompromissoId(null);
                                                    }}
                                                >
                                                    Cancelar
                                                </Button>
                                                <Button
                                                    size="xs"
                                                    bg="secondary"
                                                    color="primary"
                                                    _hover={{ bg: 'secondaryHover' }}
                                                    fontWeight="800"
                                                    leftIcon={<AiOutlineCheck />}
                                                    onClick={handleAdicionarCompromisso}
                                                >
                                                    {editandoCompromissoId ? 'Salvar' : 'Adicionar'}
                                                </Button>
                                            </HStack>
                                        </VStack>
                                    </Box>
                                ) : (
                                    <HStack spacing={2} flexWrap="wrap">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            color="textMuted"
                                            _hover={{ color: 'secondary', bg: 'surfaceHover' }}
                                            leftIcon={<AiOutlinePlus />}
                                            fontWeight="700"
                                            justifyContent="flex-start"
                                            onClick={() => setNovoCompromissoAberto(true)}
                                        >
                                            Adicionar compromisso
                                        </Button>
                                    </HStack>
                                )}

                                {/* Aviso de integração */}
                                {(formCard.compromissos || []).length > 0 && (
                                    <HStack spacing={1.5} px={1} pt={1}>
                                        <Icon as={AiOutlineInfoCircle} color="textMuted" boxSize="11px" />

                                    </HStack>
                                )}
                            </VStack>

                            {/* ── Seção: Observações ── */}
                            <SecaoLabel label="Observações" />
                            <AnotacaoFormSection
                                isEditing={!!internalCardEditando}
                                existingValue={formCard.observacoes}
                                newValue={novaAnotacao}
                                onNewValueChange={setNovaAnotacao}
                                entityLabel="negócio"
                                showSaveShortcut
                            />

                        </VStack>
                    </ModalBody>

                    <ModalFooter borderTop="1px solid" borderColor="borderPrimary" gap={3}>
                        <Button variant="ghost" color="textSecondary" _hover={{ bg: 'surfaceHover' }} onClick={handleFecharCard}>
                            Cancelar
                        </Button>
                        <Button bg="secondary" color="primary" _hover={{ bg: 'secondaryHover' }} fontWeight="800" onClick={handleSalvarCard}>
                            {internalCardEditando ? 'Salvar Alterações' : 'Criar Negócio'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Confirmação padrão: remover compromisso */}
            <ConfirmDeleteDialog
                isOpen={isDelCompOpen}
                onClose={onDelCompClose}
                onConfirm={handleConfirmarRemoverCompromisso}
                title="Remover Compromisso"
                itemName={compRemovendo?.titulo}
            />

            <AlertDialog isOpen={isConfirmEmpresaOpen} leastDestructiveRef={confirmEmpresaRef} onClose={onConfirmEmpresaClose} isCentered>
                <AlertDialogOverlay backdropFilter="blur(4px)">
                    <AlertDialogContent bg="surface" border="1px solid" borderColor="borderPrimary" color="textPrimary" borderRadius="xl">
                        <AlertDialogHeader fontSize="lg" fontWeight="900" borderBottom="1px solid" borderColor="borderPrimary">Empresa não cadastrada</AlertDialogHeader>
                        <AlertDialogBody py={5}>
                            A empresa <Text as="span" fontWeight="800" color="textPrimary">{formCard.empresa || '—'}</Text> não está cadastrada como marca no ERP. Tem certeza que deseja criar o negócio com uma empresa nova/externa?
                        </AlertDialogBody>
                        <AlertDialogFooter borderTop="1px solid" borderColor="borderPrimary" gap={3}>
                            <Button ref={confirmEmpresaRef} variant="ghost" color="textSecondary" _hover={{ bg: 'surfaceHover' }} onClick={onConfirmEmpresaClose}>Cancelar</Button>
                            <Button bg="secondary" color="primary" _hover={{ bg: 'secondaryHover' }} fontWeight="800" onClick={() => { onConfirmEmpresaClose(); handleSalvarCard(true); }}>Sim, criar</Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    );
};

export default NegocioModal;
